import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Mountain, Package, Plus, Minus, CheckCircle2, ArrowRight, Layers, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { VariantPicker, cartKey } from "../components/VariantPicker";
import { DateRangePicker } from "../components/DateRangePicker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const fmtIDR = (n) => "Rp" + Number(n||0).toLocaleString("id-ID");
const DAY_ID = ["Minggu","Senin","Selasa","Rabu","Kamis","Jumat","Sabtu"];
const MONTH_ID = ["Jan","Feb","Mar","Apr","Mei","Jun","Jul","Agt","Sep","Okt","Nov","Des"];
const fmtDateID = (iso) => { const d = new Date(iso + "T00:00:00"); return `${DAY_ID[d.getDay()]}, ${d.getDate()} ${MONTH_ID[d.getMonth()]} ${d.getFullYear()}`; };

export default function PublicBook() {
  const [settings, setSettings] = useState(null);
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [form, setForm] = useState({ customer_name: "", whatsapp: "", address: "", notes: "", acquisition_source: "" });
  const [start, setStart] = useState(new Date(Date.now()+86400000).toISOString().slice(0,10));
  const [end, setEnd] = useState(new Date(Date.now()+2*86400000).toISOString().slice(0,10));
  const [submitted, setSubmitted] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [activeCat, setActiveCat] = useState("Semua");
  const [variantProduct, setVariantProduct] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/settings`).then(r => {
      setSettings(r.data);
      document.title = "Booking Online - " + r.data.business_name;
      document.documentElement.style.setProperty("--brand-primary", r.data.primary_color || "#DC2626");
    });
  }, []);

  useEffect(() => {
    axios.get(`${API}/public/products?start=${start}&end=${end}`).then(r => setProducts(r.data)).catch(() => {});
  }, [start, end]);

  const days = Math.max(1, Math.round((new Date(end) - new Date(start))/86400000));
  const subtotal = cart.reduce((a,i) => a + i.daily_price * i.quantity * days, 0);

  const pushToCart = (p, v) => {
    const key = cartKey(p.id, v?.id);
    const max = v ? v.available : p.available;
    setCart(prev => {
      const ex = prev.find(i => i.key === key);
      if (ex) return prev.map(i => i.key === key ? {...i, quantity: Math.min(max, i.quantity + 1)} : i);
      return [...prev, {
        key, product_id: p.id, product_name: p.name, daily_price: v?.daily_price || p.daily_price, quantity: 1, max,
        variant_id: v?.id || "", variant_name: v?.name || "", image_url: v?.image_url || p.image_url || "",
        size: p.size || "", color: p.color || "",
      }];
    });
  };
  const add = (p) => {
    if (p.available <= 0) return toast.error("Stok habis");
    if (p.variants?.length) { setVariantProduct(p); return; }
    pushToCart(p, null);
  };
  const changeQty = (key, delta) => setCart(prev => prev.map(i => i.key === key ? {...i, quantity: Math.max(1, Math.min(i.max||99, i.quantity + delta))} : i));
  const remove = (key) => setCart(prev => prev.filter(i => i.key !== key));

  const openPreview = () => {
    if (!form.customer_name.trim()) return toast.error("Nama wajib diisi");
    if (!form.whatsapp.trim()) return toast.error("WhatsApp wajib diisi");
    if (!form.acquisition_source) return toast.error(`Pilih "Tahu ${settings.business_name} dari mana?"`);
    if (cart.length === 0) return toast.error("Pilih minimal 1 alat");
    setPreviewOpen(true);
  };

  const submit = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/public/bookings`, {
        customer_name: form.customer_name, whatsapp: form.whatsapp, address: form.address,
        acquisition_source: form.acquisition_source,
        items: cart.map(i => ({ product_id: i.product_id, product_name: i.product_name, quantity: i.quantity, variant_id: i.variant_id })),
        start_date: start, end_date: end, notes: form.notes,
      });
      setPreviewOpen(false);
      setSubmitted({ ...r.data, customer_name: form.customer_name });
      if (settings?.business_phone) {
        const phone = settings.business_phone.replace(/[^\d]/g, "").replace(/^0/, "62");
        const msg = encodeURIComponent(`Kak saya sudah booking atas nama ${form.customer_name}`);
        setTimeout(() => { window.open(`https://wa.me/${phone}?text=${msg}`, "_blank"); }, 400);
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal kirim booking"); }
    setLoading(false);
  };

  const UNCAT = "Tanpa Kategori";
  const catList = (() => {
    const set = new Set();
    products.forEach(p => { if (p.category) set.add(p.category); });
    const arr = Array.from(set).sort();
    const hasUncat = products.some(p => !p.category);
    return ["Semua", ...arr, ...(hasUncat ? [UNCAT] : [])];
  })();
  const filtered = products.filter(p => {
    const catOk = activeCat === "Semua" ? true : activeCat === UNCAT ? !p.category : p.category === activeCat;
    const searchOk = !q || p.name.toLowerCase().includes(q.toLowerCase()) || (p.category || "").toLowerCase().includes(q.toLowerCase());
    return catOk && searchOk;
  });

  if (!settings) return <div className="p-10 text-center">Memuat...</div>;

  if (submitted) return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-neutral-50">
      <Card className="max-w-md w-full border-neutral-200 shadow-none">
        <CardContent className="p-8 text-center">
          <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center" style={{background: "var(--brand-primary)"}}>
            <CheckCircle2 className="w-9 h-9 text-white" />
          </div>
          <h2 className="font-display text-2xl font-bold mt-4">Booking Terkirim!</h2>
          <div className="mt-3 p-3 rounded-md bg-neutral-100 font-mono-tab font-bold text-lg" data-testid="pub-booking-code">{submitted.code}</div>
          <p className="text-neutral-600 mt-4 text-sm">{submitted.message}</p>
          {settings.business_phone && (() => {
            const phone = settings.business_phone.replace(/[^\d]/g,"").replace(/^0/,"62");
            const msg = encodeURIComponent(`Kak saya sudah booking atas nama ${submitted.customer_name}`);
            return (
              <a href={`https://wa.me/${phone}?text=${msg}`} target="_blank" rel="noreferrer" data-testid="wa-admin-link">
                <Button className="w-full mt-6 text-white" style={{background: "var(--brand-primary)"}}>Kirim Pesan ke Admin via WhatsApp <ArrowRight className="w-4 h-4 ml-1" /></Button>
              </a>
            );
          })()}
          <p className="text-[10px] text-neutral-500 mt-3">Jika WhatsApp tidak terbuka otomatis, klik tombol di atas.</p>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="public-book-page">
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto p-4 flex items-center gap-3">
          {settings.logo_base64 ? <img src={settings.logo_base64} className="w-10 h-10 rounded object-cover" alt="logo" /> :
            <div className="w-10 h-10 rounded flex items-center justify-center" style={{background: "var(--brand-primary)"}}><Mountain className="w-6 h-6 text-white" /></div>}
          <div>
            <div className="font-display font-bold text-lg leading-none">{settings.business_name}</div>
            <div className="text-xs text-neutral-500 mt-1">Booking Online</div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-6 pb-40 md:pb-6">
        <div className="mb-6">
          <h1 className="font-display text-3xl md:text-4xl font-bold">Pesan Alat Outdoor</h1>
          <p className="text-neutral-600 mt-2 text-sm">Isi data, pilih alat & tanggal. Kami akan konfirmasi via WhatsApp.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-4">
            <Card className="border-neutral-200 shadow-none">
              <CardContent className="p-5 space-y-3">
                <div className="font-display font-semibold">Data Anda</div>
                <div><Label>Nama Lengkap <span className="text-red-600">*</span></Label>
                  <Input data-testid="pub-name" required value={form.customer_name} onChange={e => setForm({...form, customer_name: e.target.value})} /></div>
                <div><Label>Nomor WhatsApp <span className="text-red-600">*</span></Label>
                  <Input data-testid="pub-wa" required placeholder="08xxxxxxxxxx" value={form.whatsapp} onChange={e => setForm({...form, whatsapp: e.target.value})} /></div>
                <div><Label>Tahu {settings.business_name} dari mana? <span className="text-red-600">*</span></Label>
                  <Select value={form.acquisition_source} onValueChange={v => setForm({...form, acquisition_source: v})}>
                    <SelectTrigger data-testid="pub-source"><SelectValue placeholder="Pilih salah satu" /></SelectTrigger>
                    <SelectContent>{(settings.acquisition_sources || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label>Alamat (opsional)</Label>
                  <Textarea value={form.address} onChange={e => setForm({...form, address: e.target.value})} /></div>
                <div>
                  <Label>Tanggal Ambil → Kembali</Label>
                  <DateRangePicker start={start} end={end} onChange={(s, e) => { setStart(s); setEnd(e); }} minDate={new Date().toISOString().slice(0,10)} testId="pub-date-range" />
                </div>
                <div className="text-xs text-neutral-500 uppercase tracking-wider">Durasi: <span className="font-mono-tab font-semibold text-neutral-900">{days} hari</span></div>
                <div><Label>Catatan</Label><Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Info tambahan..." /></div>
              </CardContent>
            </Card>

            <Card className="border-neutral-200 shadow-none">
              <CardContent className="p-5">
                <div className="font-display font-semibold mb-3">Pilih Alat</div>
                <Input placeholder="Cari alat..." value={q} onChange={e => setQ(e.target.value)} className="mb-3" />
                <div className="flex gap-2 overflow-x-auto pos-scroll -mx-1 px-1 py-1 mb-3" data-testid="pub-cat-chips">
                  {catList.map(c => (
                    <button
                      key={c}
                      type="button"
                      data-testid={`pub-cat-chip-${c}`}
                      onClick={() => setActiveCat(c)}
                      className="shrink-0 px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider border transition-colors whitespace-nowrap"
                      style={activeCat === c
                        ? { background: "var(--brand-primary)", color: "#fff", borderColor: "var(--brand-primary)" }
                        : { background: "#fff", color: "#404040", borderColor: "#e5e5e5" }}
                    >
                      {c}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {filtered.length === 0 && <div className="col-span-full text-center text-sm text-neutral-500 py-8">Tidak ada alat pada kategori ini</div>}
                  {filtered.map(p => (
                    <button key={p.id} data-testid={`pub-add-${p.id}`} onClick={() => add(p)} disabled={p.available <= 0}
                      className={`text-left rounded-md border transition-colors overflow-hidden ${p.available <= 0 ? "opacity-40 cursor-not-allowed border-neutral-200 bg-neutral-50" : "border-neutral-200 hover:border-red-600 hover:bg-red-50/30 bg-white"}`}>
                      {p.image_url ? <img src={p.image_url} className="w-full h-24 object-cover" alt={p.name} /> : <div className="w-full h-24 bg-neutral-100 flex items-center justify-center"><Package className="w-8 h-8 text-neutral-300" /></div>}
                      <div className="p-3">
                        <div className="flex items-center justify-between">
                          <div className="text-xs uppercase text-neutral-500 tracking-wider">{p.category}</div>
                          {p.variants?.length > 0 && <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 flex items-center gap-0.5"><Layers className="w-2.5 h-2.5" /> {p.variants.length}</span>}
                        </div>
                        <div className="font-display font-semibold text-neutral-900 mt-1 line-clamp-2 min-h-[2.5rem] text-sm">{p.name}</div>
                        {p.variants?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {p.variants.slice(0, 4).map(v => <span key={v.id} className={`text-[9px] px-1.5 py-0.5 rounded border ${v.available > 0 ? "bg-neutral-50 text-neutral-700 border-neutral-200" : "bg-red-50 text-red-500 border-red-100 line-through"}`}>{v.name} ({v.available})</span>)}
                            {p.variants.length > 4 && <span className="text-[9px] text-neutral-400">+{p.variants.length - 4}</span>}
                          </div>
                        )}
                        <div className="flex items-end justify-between mt-2">
                          <div className="font-mono-tab font-bold text-sm" style={{color: "var(--brand-primary)"}}>{fmtIDR(p.daily_price)}</div>
                          <div className="text-xs text-neutral-500">Ready {p.available}</div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="border-neutral-200 shadow-none h-fit md:sticky md:top-24">
            <CardContent className="p-5">
              <div className="font-display font-semibold border-b border-neutral-200 pb-3">Ringkasan ({cart.length})</div>
              <div className="space-y-2 my-4 max-h-64 overflow-y-auto pos-scroll">
                {cart.map(i => (
                  <div key={i.key} className="flex items-center gap-2 py-2 border-b border-neutral-100" data-testid={`pub-cart-${i.key}`}>
                    {i.image_url ? <img src={i.image_url} alt="" className="w-9 h-9 rounded object-cover shrink-0" /> : <div className="w-9 h-9 rounded bg-neutral-100 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{i.product_name}</div>
                      {i.variant_name && <div className="text-[10px] uppercase font-bold text-blue-700">{i.variant_name}</div>}
                      <div className="text-xs text-neutral-500 font-mono-tab">{fmtIDR(i.daily_price)}/hari</div>
                    </div>
                    <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => changeQty(i.key, -1)}><Minus className="w-3 h-3" /></Button>
                    <span className="w-6 text-center font-mono-tab text-sm">{i.quantity}</span>
                    <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => changeQty(i.key, 1)}><Plus className="w-3 h-3" /></Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600" onClick={() => remove(i.key)}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                ))}
                {cart.length === 0 && <div className="text-center text-sm text-neutral-500 py-6">Belum ada alat</div>}
              </div>
              <div className="pt-3 border-t border-neutral-200">
                <div className="flex justify-between text-sm text-neutral-600"><span>Estimasi</span><span className="font-mono-tab font-bold text-lg" style={{color: "var(--brand-primary)"}}>{fmtIDR(subtotal)}</span></div>
                <p className="text-[10px] text-neutral-500 mt-1">*Total final akan dikonfirmasi oleh admin.</p>
              </div>
              <Button data-testid="pub-submit" onClick={openPreview} disabled={loading} className="w-full mt-4 h-12 text-white text-base font-semibold" style={{background: "var(--brand-primary)"}}>
                Lihat Pratinjau & Kirim
              </Button>
              <p className="text-xs text-neutral-500 mt-3 text-center">Kami akan menghubungi Anda via WhatsApp untuk konfirmasi.</p>
            </CardContent>
          </Card>
        </div>
      </main>

      <VariantPicker product={variantProduct} open={!!variantProduct} onOpenChange={(o) => !o && setVariantProduct(null)} onPick={pushToCart} />

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="booking-preview" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Pratinjau Booking</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-2 p-3 rounded-md bg-neutral-50 border border-neutral-200">
              <div><div className="text-[10px] uppercase text-neutral-500">Nama</div><div className="font-semibold">{form.customer_name}</div></div>
              <div><div className="text-[10px] uppercase text-neutral-500">WhatsApp</div><div className="font-mono-tab">{form.whatsapp}</div></div>
              <div><div className="text-[10px] uppercase text-neutral-500">Tanggal Sewa</div><div className="font-semibold">{fmtDateID(start)}</div></div>
              <div><div className="text-[10px] uppercase text-neutral-500">Tanggal Kembali</div><div className="font-semibold">{fmtDateID(end)}</div></div>
              <div className="col-span-2"><div className="text-[10px] uppercase text-neutral-500">Sumber</div><div>{form.acquisition_source}</div></div>
            </div>
            <div className="space-y-2">
              {cart.map(i => (
                <div key={i.key} className="flex items-center gap-3 border border-neutral-200 rounded-md p-2">
                  {i.image_url ? <img src={i.image_url} alt="" className="w-14 h-14 rounded object-cover" /> : <div className="w-14 h-14 rounded bg-neutral-100 flex items-center justify-center"><Package className="w-5 h-5 text-neutral-300" /></div>}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{i.product_name}</div>
                    <div className="text-xs text-neutral-500 flex flex-wrap gap-x-2">
                      {i.variant_name && <span>Varian: <b>{i.variant_name}</b></span>}
                      {i.size && <span>Ukuran: {i.size}</span>}
                      {i.color && <span>Warna: {i.color}</span>}
                    </div>
                    <div className="text-xs font-mono-tab">{i.quantity} × {fmtIDR(i.daily_price)} × {days} hari</div>
                  </div>
                  <div className="font-mono-tab font-bold">{fmtIDR(i.daily_price * i.quantity * days)}</div>
                </div>
              ))}
            </div>
            <div className="flex justify-between items-end pt-2 border-t border-neutral-200">
              <span className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">Estimasi Total</span>
              <span className="font-display font-black text-2xl font-mono-tab" style={{color: "var(--brand-primary)"}}>{fmtIDR(subtotal)}</span>
            </div>
            <Button data-testid="pub-confirm" onClick={submit} disabled={loading} className="w-full h-12 text-white font-semibold" style={{background: "var(--brand-primary)"}}>
              {loading ? "Mengirim..." : "Konfirmasi & Kirim Booking"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
